// @ts-check
import React, { useState } from "react"
import { clsx } from "../../../../../utils/strings.utils"
import ChatHistorySidebar from "./ChatHistorySidebar"
import ChatTitleModal from "./ChatTitleModal"
import Composer from "./Composer"
import EmptyState from "./EmptyState"
import Header from "./Header"
import MessageList from "./MessageList"
import { isOnlyFilesystemUriPasteText } from "./estimationAgentClipboard.utils"
import { LOCAL_NEW_CHAT_ID } from "./estimationAgentChat.constants"
import { useEstimationAgent } from "./useEstimationAgent"

export default function QuickEstimationsV3() {
  const [historyOpen, setHistoryOpen] = useState(false)
  const [titleModalOpen, setTitleModalOpen] = useState(false)

  const {
    messages,
    draft,
    setDraft,
    handleSend,
    handleStop,
    isCreatingChat,
    isResponseStreaming,
    headerSubtitle,
    subtitleLoading,
    chatError,
    chat,
    activeChatId,
    selectChat,
    isLoadingChatDetail,
    loadChatDetailError,
    addToolApprovalResponse,
    addToolOutput,
    composerLocked,
    composerAttachments,
    removeComposerAttachment,
    addComposerFiles,
    isComposerUploading,
    notifyPasteFilesystemUriOnly,
    syncChatTitle,
  } = useEstimationAgent()

  const hasReadyAttachments = composerAttachments.some((a) => a.phase === "ready")

  const isEmpty = messages.length === 0
  const errorBannerText =
    loadChatDetailError ||
    (chatError ? chatError.message || "Error al conectar con el asistente" : null)

  return (
    <div
      className={clsx(
        "estimation-agent-quick-v3",
        isEmpty && !isLoadingChatDetail && "estimation-agent-quick-v3--empty"
      )}
    >
      <Header
        title="Asistente de estimación"
        subtitle={headerSubtitle}
        subtitleLoading={subtitleLoading}
        onOpenHistory={() => setHistoryOpen(true)}
        canEditSubtitle={Boolean(activeChatId && headerSubtitle && !subtitleLoading)}
        onEditSubtitle={() => setTitleModalOpen(true)}
      />

      <ChatHistorySidebar
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        activeChatId={activeChatId}
        activeChatIsNuevo={isEmpty && !chat?.title?.trim()}
        onSelectChat={async (chatId) => {
          const ok = await selectChat(chatId)
          if (ok) setHistoryOpen(false)
        }}
        onChatTitleUpdated={(chatId, title) => syncChatTitle(chatId, title)}
        onChatDeleted={async (chatId) => {
          if (activeChatId === chatId) await selectChat(LOCAL_NEW_CHAT_ID)
        }}
      />

      <ChatTitleModal
        visible={titleModalOpen}
        chatId={activeChatId}
        initialTitle={chat?.title}
        onHide={() => setTitleModalOpen(false)}
        onSaved={(title) => {
          if (activeChatId) syncChatTitle(activeChatId, title)
        }}
      />

      <div className="estimation-agent-quick-v3__main">
        <div className="estimation-agent-quick-v3__body">
          {isLoadingChatDetail ?
            <div
              className="estimation-agent-quick-v3__chat-detail-loading"
              role="status"
              aria-live="polite"
            >
              <i
                className="pi pi-spinner pi-spin estimation-agent-quick-v3__chat-detail-loading-icon"
                aria-hidden
              />
              <span>Cargando chat…</span>
            </div>
          : messages.length > 0 || errorBannerText ?
            <MessageList
              messages={messages}
              streaming={isResponseStreaming}
              errorBanner={errorBannerText}
              addToolApprovalResponse={addToolApprovalResponse}
              addToolOutput={addToolOutput}
            />
          : <EmptyState>¿De qué trata el proyecto?</EmptyState>}
        </div>

        <Composer
          value={draft}
          onChange={(e) => {
            const v = e.target.value
            if (isOnlyFilesystemUriPasteText(v)) {
              setDraft("")
              notifyPasteFilesystemUriOnly()
              return
            }
            setDraft(v)
          }}
          onSend={handleSend}
          onStop={handleStop}
          isCreatingChat={isCreatingChat}
          isResponseStreaming={isResponseStreaming}
          isLoadingChatDetail={isLoadingChatDetail}
          composerLocked={composerLocked}
          attachments={composerAttachments}
          onRemoveAttachment={removeComposerAttachment}
          onFilesSelected={(files) => {
            addComposerFiles(files).catch((e) => console.error(e))
          }}
          onPasteFilesystemUriOnly={notifyPasteFilesystemUriOnly}
          isComposerUploading={isComposerUploading}
          hasReadyAttachments={hasReadyAttachments}
        />
      </div>
    </div>
  )
}
